import type { LucideIcon } from "lucide-react";
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from "lucide-react";
import type { ReactNode } from "react";
import { type StatusVariant, statusClasses } from "@/lib/colors";
import { cn } from "@/lib/utils";

// NoticeTone mapuje 1:1 na StatusVariant z semantic tokenow.
// statusClasses uzywa var(--status-{tone}-{bg|text|border}) zdefiniowanych
// w globals.css — zgodnie z frontend/AGENTS.md "no hardcoded colors".
type NoticeTone = StatusVariant;

interface NoticeBannerProps {
	tone: NoticeTone;
	icon?: LucideIcon;
	title: ReactNode;
	description?: ReactNode;
	action?: ReactNode;
	onDismiss?: () => void;
	className?: string;
}

const DEFAULT_ICONS: Record<NoticeTone, LucideIcon> = {
	info: Info,
	warning: AlertTriangle,
	error: AlertCircle,
	success: CheckCircle,
};

export function NoticeBanner({
	tone,
	icon,
	title,
	description,
	action,
	onDismiss,
	className,
}: NoticeBannerProps) {
	const Icon = icon ?? DEFAULT_ICONS[tone];
	const role = tone === "error" ? "alert" : "status";
	return (
		<div
			role={role}
			className={cn(
				"flex items-center gap-3 rounded-md border px-3 py-2 text-sm",
				statusClasses[tone],
				className,
			)}
		>
			<Icon className="size-4 shrink-0" />
			<div className="flex-1 min-w-0">
				<span className="font-medium">{title}</span>
				{description ? <span className="ml-1 opacity-80">{description}</span> : null}
			</div>
			{action}
			{onDismiss ? (
				<button
					type="button"
					onClick={onDismiss}
					className="p-1 hover:opacity-80"
					aria-label="Zamknij"
				>
					<X className="size-3.5" />
				</button>
			) : null}
		</div>
	);
}
